<script setup>
import { ref, inject, getCurrentInstance, computed } from "vue";
import NProgress from "nprogress";
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { Bars3Icon, XMarkIcon } from "@heroicons/vue/24/outline";
import Notifications from "@Components/Notifications.vue";
import MissingSettingsModal from "@Components/Modal/MissingSettingsModal.vue";
import Loading from "@Components/Animation/Loading.vue";
import PricingPlansModal from "@Components/Modal/PricingPlansModal.vue";
import AppLink from "@Components/AppLink.vue";
import TrialCard from "./Partials/TrialCard.vue";
import IssuesBanner from "@Components/IssuesBanner.vue";
import AppLogo from "@Components/AppLogo.vue";
import { useRoute } from "vue-router";
import OneClickSidebar from "./Sidebars/OneClickSidebar.vue";
import SingleModeSidebar from "./Sidebars/SingleModeSidebar.vue";
import DefaultSidebar from "./Sidebars/DefaultSidebar.vue";
import PremiumModal from "@Components/Modal/PremiumModal.vue";

const route = useRoute();

const sidebarOpen = ref(false);

const pending = () => {
    NProgress.start();
};
const resolve = () => {
    NProgress.done();
};
const fallback = () => {
    NProgress.done();
};

const notifications = ref();
const setNotificationComponent = inject("setNotificationComponent");
setNotificationComponent(notifications);

const pricingPlans = ref();
const setPricingPlansComponent = inject("setPricingPlansComponent");
setPricingPlansComponent(pricingPlans);

const sidebarComponent = computed(() => {
    if (route.name.match(/^one-click/)) {
        return OneClickSidebar;
    } else if (route.name.match(/^single-mode/)) {
        return SingleModeSidebar;
    }

    return DefaultSidebar;
});
</script>

<template>
    <div>
        <TransitionRoot as="template" :show="sidebarOpen">
            <Dialog as="div" class="z-[100000] relative xl:hidden" @close="sidebarOpen = false">
                <TransitionChild
                    as="template"
                    enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leave-from="opacity-100"
                    leave-to="opacity-0">
                    <div class="bg-gray-900/80 fixed inset-0" />
                </TransitionChild>

                <div class="fixed inset-0 flex">
                    <TransitionChild
                        as="template"
                        enter="transition ease-in-out duration-300 transform"
                        enter-from="-translate-x-full"
                        enter-to="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leave-from="translate-x-0"
                        leave-to="-translate-x-full">
                        <DialogPanel class="w-full max-w-xs relative flex flex-1 mr-16">
                            <TransitionChild
                                as="template"
                                enter="ease-in-out duration-300"
                                enter-from="opacity-0"
                                enter-to="opacity-100"
                                leave="ease-in-out duration-300"
                                leave-from="opacity-100"
                                leave-to="opacity-0">
                                <div class="w-16 absolute top-0 left-full flex justify-center pt-5">
                                    <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                                        <span class="sr-only">Close sidebar</span>
                                        <XMarkIcon class="w-6 h-6 text-white" aria-hidden="true" />
                                    </button>
                                </div>
                            </TransitionChild>
                            <div class="w-72 flex flex-col grow gap-y-5 bg-white ring-1 ring-gray-300 dark:bg-gray-900 dark:ring-gray-800">
                                <component :is="sidebarComponent" />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Static sidebar for desktop -->
        <div class="hidden bg-white ring-1 ring-gray-300 dark:bg-gray-900 dark:ring-gray-800 xl:z-[100000] xl:w-72 xl:fixed xl:inset-y-0 xl:block">
            <component :is="sidebarComponent" />
        </div>

        <div class="flex">
            <div class="overflow-auto flex flex-col flex-1 xl:pl-72">
                <div
                    class="h-16 sticky top-0 z-40 flex shrink-0 justify-center items-center gap-x-6 px-4 bg-white border-b border-gray-300 shadow-sm dark:bg-gray-900 dark:border-gray-800 lg:px-8 sm:px-6 xl:hidden">
                    <button type="button" class="absolute left-2.5 p-2.5 text-gray-900 dark:text-white" @click="sidebarOpen = true">
                        <span class="sr-only">Open sidebar</span>
                        <Bars3Icon class="w-5 h-5" aria-hidden="true" />
                    </button>

                    <AppLink :to="{ name: 'dashboard' }">
                        <AppLogo />
                    </AppLink>
                </div>
                <main class="border-white/10 min-h-[calc(100vh-4rem)] flex-1 border-t xl:min-h-screen xl:border-t-0">
                    <IssuesBanner
                        v-if="getCurrentInstance().appContext.config.globalProperties.$app.issues.length > 0"
                        :issues="getCurrentInstance().appContext.config.globalProperties.$app.issues" />
                    <TrialCard />
                    <RouterView v-slot="{ Component }">
                        <Transition name="fade-75" mode="out-in">
                            <Suspense @pending="pending" @resolve="resolve" @fallback="fallback">
                                <Component :is="Component" :key="$route" />
                                <template #fallback>
                                    <div class="min-h-[calc(100vh-4rem)] flex justify-center items-center xl:min-h-screen">
                                        <Loading class="w-24 h-24 opacity-25 animate-pulse" />
                                    </div>
                                </template>
                            </Suspense>
                        </Transition>
                    </RouterView>
                </main>
            </div>
            <div id="content-sidebar-root" class="flex-none"></div>
        </div>

        <MissingSettingsModal />

        <PremiumModal />

        <Notifications ref="notifications" />
        <PricingPlansModal ref="pricingPlans" />
    </div>
</template>
